import { Link as RouterLink } from "react-router";
import Link from "@mui/material/Link";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { getOrderById } from "@/api/order/getOrderById";
import { toTitleCase } from "@/app/text.utils";
import { DetailDrawer, useDetailStackDrawer, useDetailStackId } from "@/components/detail-stack";
import { DETAIL_KIND } from "@/features/detail/detailKind";
import type { DetailKind } from "@/features/detail/detailKind";
import { OrderListExpanded } from "@/features/order/order-list/order-list-expanded/OrderListExpanded";
import { OrderListStatusChip } from "@/features/order/order-list/order-list-status-chip/OrderListStatusChip";
import { OrderListSystemChip } from "@/features/order/order-list/order-list-system-chip/OrderListSystemChip";

/**
 * One order's details, an `orders/<id>` level of the detail stack (fetched by id, GET /orders/{orderId}):
 * number, origin, status and customer, then the expanded row's sections. The customer's name opens the customer on top.
 */
export const OrderDetail = () => {
  const level = useDetailStackDrawer<DetailKind>();
  const orderId = useDetailStackId();
  const query = getOrderById.useQuery(orderId);
  const order = query.data;
  const customerOrder = order?.customerOrder;
  let customerName: string | undefined;
  if (customerOrder?.customerName) customerName = toTitleCase(customerOrder.customerName);

  const title = (
    <>
      {order && (
        <Stack
          direction="row"
          spacing={1.5}
          sx={{ alignItems: "center" }}
        >
          <Typography
            sx={{ fontWeight: 600 }}
            variant="h6"
          >
            {order.orderNumber || "—"}
          </Typography>
          {order.source && <OrderListSystemChip system={order.source} />}
          <OrderListStatusChip status={order.status} />
        </Stack>
      )}
      {query.isError && (
        <Typography
          sx={{ fontWeight: 600 }}
          variant="h6"
        >
          Order not found
        </Typography>
      )}
      {customerOrder && customerName && (
        <Link
          component={RouterLink}
          to={level.toChild(DETAIL_KIND.customers, customerOrder.customerId)}
          underline="hover"
          variant="body2"
        >
          {customerName}
        </Link>
      )}
    </>
  );

  return (
    <DetailDrawer title={title}>
      {order && <OrderListExpanded order={order} />}
      {query.isError && (
        <Typography variant="body2">
          There's no order with this id.
        </Typography>
      )}
    </DetailDrawer>
  );
};
